import { useState } from 'react';
import { Button, Drawer, ChoiceList } from '@cmsgov/ds-medicare-gov';
import { trackFiltersApplied } from '@shared/analytics';
import type { Facets, FacetTerm } from '../types';
import type { Lang, FacetKey } from '../constants';
import { FACETS } from '../constants';
import { t } from '../i18n';
import './FilterDrawer.css';

interface FilterDrawerProps {
  lang: Lang;
  facets: Facets | null;
  activeFilters: Record<FacetKey, number[]>;
  totalResults: number;
  onApply: (filters: Record<FacetKey, number[]>) => void;
  onClear: () => void;
}

const FACET_ORDER: FacetKey[] = [
  FACETS.PRICE_TYPE,
  FACETS.PLATFORMS,
  FACETS.CONDITION,
  FACETS.FEATURES,
];

export function FilterDrawer({
  lang,
  facets,
  activeFilters,
  totalResults,
  onApply,
  onClear,
}: FilterDrawerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [pending, setPending] = useState<Record<FacetKey, number[]>>(activeFilters);
  const strings = t(lang);

  // Sync pending selections when activeFilters change externally
  // (e.g. a FilterSet apply, chip removal, or full reset from the parent).
  // Uses setState-during-render to avoid a useEffect cascade:
  // React detects the state change, discards the current render, and
  // immediately re-renders with the updated pending state.
  const [prevFiltersJSON, setPrevFiltersJSON] = useState(
    () => JSON.stringify(activeFilters)
  );
  const currentFiltersJSON = JSON.stringify(activeFilters);
  if (prevFiltersJSON !== currentFiltersJSON) {
    setPrevFiltersJSON(currentFiltersJSON);
    setPending({ ...activeFilters });
  }

  // Open the drawer to show filter options.
  const handleOpen = () => {
    setIsOpen(true);
  };

  // Apply the pending filters, close the drawer, and track analytics.
  const handleApply = () => {
    onApply(pending);
    setIsOpen(false);

    const usedCategories = FACET_ORDER
      .filter((key) => (pending[key] || []).length > 0)
      .map((key) => strings.facetLabels[key])
      .join(', ');

    if (usedCategories) {
      trackFiltersApplied({
        filterCategoriesUsed: usedCategories,
        totalResults,
        countBeforeFilter: totalResults,
        countAfterFilter: 0,
      });
    }
  };

  // Clear all filters and close the drawer.
  const handleClear = () => {
    onClear();
    setIsOpen(false);
  };

  // Toggle a single tid in/out of the pending selection for a given facet.
  // Changes are held locally until the user clicks Apply.
  const handleChoiceChange = (facetKey: FacetKey, tid: number) => {
    setPending((prev) => {
      const current = prev[facetKey] || [];
      if (current.includes(tid)) {
        return { ...prev, [facetKey]: current.filter((id) => id !== tid) };
      }
      return { ...prev, [facetKey]: [...current, tid] };
    });
  };

  // Calculate total count of active filters for display on the toggle button.
  const activeCount = Object.values(activeFilters).reduce((sum, arr) => sum + arr.length, 0);

  return (
    <div className="app-library-search__filter-drawer ds-u-lg-display--none">
      <div className="app-library-search__filter-drawer-container">
        <Button onClick={handleOpen} disabled={!facets} className={`app-library-search__filter-drawer-toggle${activeCount > 0 ? ' app-library-search__filter-drawer-toggle--active' : ''}`}>
          <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" viewBox="0 0 640 640"><path d="M96 128C83.1 128 71.4 135.8 66.4 147.8C61.4 159.8 64.2 173.5 73.4 182.6L256 365.3L256 480C256 488.5 259.4 496.6 265.4 502.6L329.4 566.6C338.6 575.8 352.3 578.5 364.3 573.5C376.3 568.5 384 556.9 384 544L384 365.3L566.6 182.7C575.8 173.5 578.5 159.8 573.5 147.8C568.5 135.8 556.9 128 544 128L96 128z"/></svg>
          {strings.filterButton}
          {activeCount > 0 && (
            <span className="app-library-search__filter-drawer-toggle-count">
              ({activeCount})
            </span>
          )}
        </Button>
        <Drawer
          heading={strings.filterBy}
          isOpen={isOpen}
          onCloseClick={() => setIsOpen(false)}
          footerBody={
            <div className="app-library-search__filter-drawer-footer">
              <Button onClick={handleApply} variation="solid" className="ds-c-button--alternate app-library-search__filter-drawer-footer-apply">
                {strings.applyFilters}
              </Button>
              <Button variation="ghost" onClick={handleClear} className="app-library-search__filter-drawer-footer-clear">
                {strings.clearAllFilters}
              </Button>
            </div>
          }
        >
          {facets && FACET_ORDER.map((facetKey) => {
            const terms: FacetTerm[] = facets[facetKey] || [];

            return (
              // Each ChoiceList renders one filter category (e.g. "Key Features").
              // value is the term's primary tid as a string. data-hte-tids is set
              // on app_key_features terms that reference app_hte_categories terms
              // via field_related_hte_category; the backend expands those tids
              // automatically when the features param is received.
              <ChoiceList
                key={facetKey}
                type="checkbox"
                name={`drawer-${facetKey}`}
                label={strings.facetLabels[facetKey]}
                choices={terms.map((term) => ({
                  label: term.name,
                  value: String(term.tid),
                  checked: (pending[facetKey] || []).includes(term.tid),
                  disabled: !term.relevant,
                  ...(term.related_hte_category?.length
                    ? { 'data-hte-tids': term.related_hte_category.map((r) => r.tid).join(',') }
                    : {}),
                }))}
                onChange={(e) => {
                  handleChoiceChange(facetKey, Number(e.currentTarget.value));
                }}
                className="app-library-search__filter-drawer-choice-list"
              />
            );
          })}
        </Drawer>
      </div>
    </div>
  );
}
